import { SetStateAction } from "react";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { DispatchNotificationAction } from "@States/notifications";
import { ApiErrorResponse } from "@Types/api/error";
import { LoginProvider, ScreenParams } from "@Types/login";
import { checkEmailFormat } from "@Utils/checkFormats";
import { errorManager } from "@Utils/error";
import { getMetaContent } from "@Utils/meta";
import { convertToIdentifyProperties, setDataLayerTrack } from "@Utils/track";
import { convertUserCheckedFromApiToContent, convertUserFromApiToContent } from "@Utils/user";

const cheerzApiProvider = (locale: string): CheerzApiProvider => {
  const csrfToken = getMetaContent("csrf-token");

  if (!csrfToken) {
    throw new Error("CSRF Token not found");
  }

  return new CheerzApiProvider(locale, csrfToken);
};

export const logNewUserWithProvider = (
  params: { email: string; provider: LoginProvider; token: string },
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  dispatchNotification: DispatchNotificationAction,
  setDisabled: (value: SetStateAction<boolean>) => void,
  locale: string
): void => {
  const redirection = (validatedEmail: string): void => {
    cheerzApiProvider(locale)
      .omniauthClient(validatedEmail, params.token, params.provider)
      .then((response) => {
        const userConnected = convertUserFromApiToContent(response);

        setDataLayerTrack("Identify", convertToIdentifyProperties(userConnected));
        onChangeScreen({
          action: "signUp",
          disabledAction: false,
          firstName: userConnected.firstName,
          screen: "welcome",
        });
      })
      .catch((error: ApiErrorResponse) => {
        errorManager(
          dispatchNotification,
          "CFE-001",
          error.response?.data?.error_description[0] ?? undefined
        );
        setDisabled(false);
      });
  };

  checkEmailFormat(params.email)
    .then((validatedEmail) => redirection(validatedEmail))
    .then(() => {
      if (params.provider) {
        window.localStorage.setItem("showNewsletterModal", "true");
      }
    })
    .catch((errorCode: string) => {
      errorManager(dispatchNotification, errorCode);
      setDisabled(false);
    });
};

export const logUserWithEmail = (
  email: string,
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  dispatchNotification: DispatchNotificationAction,
  setDisabled: (value: SetStateAction<boolean>) => void,
  locale: string
): void => {
  const redirection = (validatedEmail: string): void => {
    cheerzApiProvider(locale)
      .checkUser({ email: validatedEmail })
      .then((response) => {
        onChangeScreen({
          action: convertUserCheckedFromApiToContent(response).isExisting ? "signIn" : "signUp",
          disabledAction: false,
          email: response.email,
          screen: "password",
        });
      })
      .catch((error: ApiErrorResponse) => {
        errorManager(
          dispatchNotification,
          "CFE-001",
          error.response?.data?.error_description[0] ?? undefined
        );
        setDisabled(false);
      });
  };

  checkEmailFormat(email)
    .then((validatedEmail) => redirection(validatedEmail))
    .catch((errorCode: string) => {
      errorManager(dispatchNotification, errorCode);
      setDisabled(false);
    });
};
